import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightLong } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";
import AppRoutes from "../../../../routes/AppRoutes";
import { Badge } from "../../../common";
import SuccessAlert from "../../../alerts/SuccessAlert";
import DangerAlert from "../../../alerts/DangerAlert";
import { ManagerTeamTodosSectionDto } from "../../../../types/dtos/dashboards/right-bar/ManagerTeamTodosSectionDto";
import useManagerTodoActions from "../../../manager-dashboard/shared/useManagerTodoActions";
import {
  filterManagerTodos,
  sortManagerTodosByNewest,
} from "../../../manager-dashboard/shared/managerTodoHelper";
import ManagerTeamTodoSidebarItem from "./ManagerTeamTodoSidebarItem";
import ManagerTeamTodosEmptyState from "./ManagerTeamTodosEmptyState";
import { MANAGER_TEAM_WIDGET_MAX_DISPLAY_ITEMS } from "./managerTeamWidgetConstants";

interface DashboardManagerTeamWidgetProps {
  section: ManagerTeamTodosSectionDto;
  onTodosChanged: () => void;
}

/** Manager-only compact To Do preview for the employee homepage right bar. */
function DashboardManagerTeamWidget({
  section,
  onTodosChanged,
}: DashboardManagerTeamWidgetProps) {
  const { t } = useTranslation();

  const todos = useMemo(
    () => sortManagerTodosByNewest(filterManagerTodos(section.updates)),
    [section.updates]
  );
  const displayTodos = todos.slice(0, MANAGER_TEAM_WIDGET_MAX_DISPLAY_ITEMS);

  const {
    handleSkipConfirmationModalClick,
    handleLoadDualPrepCollabDoc,
    skipPlanningPopUp,
    showSuccessNotification,
    successNotificationMessage,
    showErrorNotification,
    errorNotificationMessage,
  } = useManagerTodoActions(section.users, onTodosChanged);

  const hasTeamTodos = todos.length > 0;

  return (
    <>
      <div
        id="manager-team-list"
        className={cx(
          "rounded-lg p-4 mb-4",
          hasTeamTodos
            ? "background-tint-bg"
            : "bg-white"
        )}
      >
        <div className="flex flex-row">
          <header className="text-lg font-semibold mt-0 mb-2 basis-9/12">
            {t("DashboardRightBar.YourTeam")}
            <Badge
              text={todos.length.toString()}
              backgroundColourClassName="accent-bg"
              textColourClassName="text-white"
            />
          </header>
          <div className="basis-3/12 text-right">
            <Link
              to={AppRoutes.yourPeople.root}
              className="group inline-flex items-center hover:cursor-pointer"
            >
              <span className="text-sm group-hover:underline">{t("Common.ViewAll", "View all")}</span>
              <FontAwesomeIcon
                icon={faArrowRightLong}
                className="ml-1"
                size="xs"
              />
            </Link>
          </div>
        </div>

        {todos.length === 0 ? (
          <ManagerTeamTodosEmptyState />
        ) : (
          <div className="flex flex-col gap-2">
            {displayTodos.map((update, index) => (
              <ManagerTeamTodoSidebarItem
                key={`${update.updateType}_${update.userId}_${update.clientFormTitle}_${index}`}
                update={update}
                users={section.users}
                onDualPrepClick={handleLoadDualPrepCollabDoc}
                onSkipPlanningClick={handleSkipConfirmationModalClick}
              />
            ))}
          </div>
        )}

        {showSuccessNotification && (
          <SuccessAlert
            prefix={t("Common.Success")}
            message={successNotificationMessage}
            includeMarginTop={true}
          />
        )}
        {showErrorNotification && (
          <DangerAlert
            prefix={t("Common.Validation.Oops")}
            message={t(errorNotificationMessage)}
          />
        )}
      </div>
      {skipPlanningPopUp}
    </>
  );
}

export default DashboardManagerTeamWidget;
